// Packages
import React, { useEffect, useState, useContext } from "react";
import { connect } from "react-redux";
import { Redirect } from "react-router-dom";
import Pusher from "pusher-js";
import { FormattedMessage } from 'react-intl';
// Components
import InlineLoader from "../../mobileComponents/mobileInlineLoader";
import OrderPlacedPopup from "../../mobileComponents/orderPlacedPopup";
// Redux Operations
import { pageOperations } from "../../state/features/page";
import { paymentTypeOperations } from "../../state/features/paymentTypes";
import { cartOperations } from "../../state/features/cart";
// Contexts
import { EnvContext } from "../../components/envContext";
import { MASTERCARD_PAYMENT_TYPE } from "../../helpers/constants";
import { clearCartStorage } from "../../helpers/cartFunctions";
import { getSubdomainPath } from "../../helpers/pathUtils";

interface PaymentProcessingMobileProps {
  location: any;
  fetchPaymentProcessingConfigurations: any;
  verifyPayment: any;
  setCart: any;
  checkGenericPaymentStatus: any;
  checkGenericStandalonePaymentStatus: any;
}

function PaymentProcessingMobile(props: PaymentProcessingMobileProps) {
  const [paymentStatus, setPaymentStatus] = useState("in_progress");
  const [earnedLoyalty, setEarnedLoyalty] = useState(null as any);
  const [redirect, setRedirect] = useState(false);
  const [redirectConfig, setRedirectConfig] = useState({});
  const [searchQueryLoaded, setSearchQueryLoaded] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [tmpOrderID, setTmpOrderID] = useState(null);
  const [paymentReference, setPaymentReference] = useState<any>("");
  const [paymentType, setPaymentType] = useState<any>("");
  const [standalone, setStandalone] = useState(false);

  const { pusherId } = useContext(EnvContext);

  useEffect(() => {
    if (props.location) {
      let urlParams = new URLSearchParams(props.location.search);
      let reference = urlParams.get("reference");
      let type = urlParams.get("type");
      let standaloneParam = urlParams.get("standalone");

      setPaymentReference(reference);
      setPaymentType(type);
      setStandalone(standaloneParam === "true");
      setSearchQueryLoaded(true);
    }
  }, [props.location]);

  useEffect(() => {
    if (searchQueryLoaded) {
      // Fetch Configurations for processing payment
      props
        .fetchPaymentProcessingConfigurations(paymentReference, paymentType)
        .then((response: any) => {
        if (!response.error) {
          const paymentDetails = response.payload.data?.payment_details;

          setPaymentReference(paymentDetails?.reference);
          setPaymentType(paymentDetails?.type);
          setPaymentStatus(paymentDetails?.status);
          setEarnedLoyalty(paymentDetails?.earned_loyalty);
          setTmpOrderID(paymentDetails?.tmp_order_id);

          setConfigLoaded(true);
        }
      });
    } 
  }, [searchQueryLoaded]);

  useEffect(() => {
    let statusTimeOut: any;
    if (configLoaded && paymentStatus) {
      processPaymentStatus();
      if (paymentType == "generic_fintech_partner") {
        statusTimeOut = setTimeout(() => {
          if (paymentStatus == "in_progress") {
            props.checkGenericPaymentStatus(tmpOrderID);
          }
        }, 30000);
      } else if (standalone) {
        statusTimeOut = setTimeout(() => {
          if (paymentStatus == "in_progress") {
            props.checkGenericStandalonePaymentStatus(tmpOrderID, paymentType);
          }
        }, 30000);
      }
    }
    return () => clearTimeout(statusTimeOut);
  }, [configLoaded, paymentStatus]);

  const processPaymentStatus = () => {
    switch (paymentStatus) {
      case "in_progress":
        bindToPusherChannel();
        break;
      case "paid":
        resetCart();
        break;
      case "declined":
        performRedirection("declined");
        break;
      case "cancelled":
        performRedirection("cancelled");
        break;
    }
  }

  const bindToPusherChannel = () => {
    // Connect to Pusher
    const pusher = new Pusher(pusherId, {});
    let channel
    
    // For standalone payments, always use the generic channel regardless of payment type
    if (standalone) {
      channel = pusher.subscribe(`generic_payment_transaction_${paymentReference}`);
    } else {
      // Subscribe to respective payment channel for non-standalone payments
      switch (paymentType) {
        case "paytabs":
          channel = pusher.subscribe(`paytabs_payment_${paymentReference}`);
          break;
        case MASTERCARD_PAYMENT_TYPE:
          channel = pusher.subscribe(`mastercard_payment_${paymentReference}`);
          break;  
        case "pay_u":
          channel = pusher.subscribe(`pay_u_payment_${paymentReference}`);
          break;
        case "i_pay88":
          channel = pusher.subscribe(`i_pay88_payment_${paymentReference}`);
          break;
        case "adyen":
          channel = pusher.subscribe(`adyen_payment_${paymentReference}`);
          break;
        case "geidea":
          channel = pusher.subscribe(`geidea_payment_${paymentReference}`);
          break;
        case "stripe_connect":
          channel = pusher.subscribe(`private_stripe_connect_transaction_${paymentReference}`);
          break;
        case "generic_fintech_partner":
          channel = pusher.subscribe(`generic_payment_transaction_${paymentReference}`);
          break;
      }
    }

    if (channel) {
      const verifyPayment = () => {
        // Check status of payment once timeout exceeded
        props.verifyPayment(tmpOrderID).then((response: any) => {
          if (!response.error) {
            const paymentDetails = response.payload.data;

            if (paymentDetails?.status != "in_progress") {
              channel.unbind();

              clearInterval(periodicStatusChecker);
              clearTimeout(timeOutHandler);

              setPaymentStatus(paymentDetails?.status);
              setEarnedLoyalty(paymentDetails?.earned_loyalty);
            }
          }
        });
      }

      // Initial Check in case Pusher message received before Loading pusher
      var initialStatusChecker = setTimeout(function(){
        clearTimeout(initialStatusChecker);
        
        verifyPayment();
      }, 5000);

      // Polls every few seconds if pusher message is not received
      var periodicStatusChecker = setInterval(function(){
        clearTimeout(initialStatusChecker);
        
        verifyPayment();
      }, 30000);

      // Handle Timeout
      var timeOutHandler = setTimeout(function(){
        clearInterval(periodicStatusChecker);
        clearTimeout(initialStatusChecker);
        clearTimeout(timeOutHandler);
        channel.unbind();

        setPaymentStatus("declined");
      }, 305000);

      // Handle Successful Payment
      channel.bind("payment_succeeded", (data: any) => {
        clearInterval(periodicStatusChecker);
        clearTimeout(initialStatusChecker);
        clearTimeout(timeOutHandler);

        setPaymentStatus("paid");
        setEarnedLoyalty(data.earned_loyalty);
      });

      // Handle Failed Payment
      channel.bind("payment_failed", (data: any) => {
        clearInterval(periodicStatusChecker);
        clearTimeout(initialStatusChecker);
        clearTimeout(timeOutHandler);

        setPaymentStatus("declined");
      });

      // Handle cancelled payment
      channel.bind("payment_cancelled", (data: any) => {
        clearInterval(periodicStatusChecker);
        clearTimeout(initialStatusChecker);
        clearTimeout(timeOutHandler);

        setPaymentStatus("cancelled");
      });
    }
  }

  const resetCart = () => {
    clearCartStorage();
    props.setCart({ items: [], subTotal: 0, total: 0 });
  }

  const performRedirection = (reason: any) => {
    switch (reason) {
      case "declined":
      case "cancelled":
        const orderDetailsPath = getSubdomainPath("/m/order-details");
        setRedirectConfig({
          pathname: orderDetailsPath,
          search: "?payment_status=failed",
        });
      break;
    }
    setTimeout(() => {
      setRedirect(true);
    }, 5000);
  };

  return (
    <React.Fragment>
      {redirect ? <Redirect to={redirectConfig} /> : null}
      <div className="payment-processing-mobile">
        <InlineLoader />
        {paymentStatus == "in_progress" ? (
          <React.Fragment>
            <h1 className="status-title">
              <FormattedMessage
                id="payment.in_progress"
                defaultMessage="Payment in Progress"
              />
            </h1>
            <p className="status-message">
              <FormattedMessage
                id="payment.processing_do_not_close_or_reload"
                defaultMessage="We are processing your payment. Please do not close or reload this page."
              />
            </p>
          </React.Fragment>
        ) : paymentStatus == "paid" ? (
          <OrderPlacedPopup earnedLoyalty={earnedLoyalty} />
        ) : (
          <React.Fragment>
            <h1 className="status-title">
              <FormattedMessage
                id="payment.declined"
                defaultMessage="Payment Declined"
              />
            </h1>
            <p className="status-message">
              <FormattedMessage
                id="payment.sorry"
                defaultMessage="Sorry."
              />
            </p>
            <p className="status-message">
              <FormattedMessage
                id="payment.declined_redirect_to_cart"
                defaultMessage="Your payment has been declined. You will be redirected to the cart. Please do not close or reload this tab."
              />
            </p>
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  );
}

const mapDispatchToProps = {
  fetchPaymentProcessingConfigurations:
    pageOperations.fetchPaymentProcessingConfigurations,
  verifyPayment: paymentTypeOperations.verifyPayment,
  setCart: cartOperations.setCart,
  checkGenericPaymentStatus: paymentTypeOperations.checkGenericPaymentStatus,
  checkGenericStandalonePaymentStatus: paymentTypeOperations.checkGenericStandalonePaymentStatus,
};

export default connect(null, mapDispatchToProps)(PaymentProcessingMobile);
